import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { useDataSourceSnapshot } from '@/features/data-sources/api'
import { DataExplorer } from '@/features/data-sources/components/data-explorer'
import { parsePath, valueAt } from '@/features/data-sources/lib/json-path'
import type { RuleCatalogueEntry } from '@/types/api'
import type { Family } from '../lib/model'

/** Comparison family suggested by a sample value (the ref itself stays dynamic). */
export function familyOfSample(v: unknown): Family | null {
  if (typeof v === 'number') return 'number'
  if (typeof v === 'boolean') return 'boolean'
  if (Array.isArray(v)) return 'list'
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return 'date'
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(v)) return 'time'
    if (/^-?(0|[1-9]\d*)(\.\d+)?$/.test(v)) return 'number'
    return 'text'
  }
  return null
}

/**
 * Data Explorer drill-down for `data.<key>` refs: pick one value of the source's latest data
 * (keyed lists use `[?field=value]`). "Data status" uses the whole source (`freshness_is`).
 */
export function DataPathDialog({
  source,
  open,
  onOpenChange,
  onPick,
}: {
  source: RuleCatalogueEntry | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onPick: (ref: string, family: Family | null) => void
}) {
  const [path, setPath] = useState<string | null>(null)
  const key = source?.ref.replace(/^data\./, '') ?? ''
  const snapshot = useDataSourceSnapshot(source?.source_id ?? null, open && !!source?.source_id)
  const sample = (p: string): unknown => {
    const parsed = parsePath(p)
    const payload = snapshot.data?.payload
    if (!parsed || payload === undefined) return undefined
    const hit = valueAt(payload, parsed.segments)
    return hit.found ? hit.value : undefined
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) setPath(null)
        onOpenChange(o)
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Choose a value from {source?.label ?? key}</DialogTitle>
          <DialogDescription>Click a value in the latest data. Lists can be identified by a field (e.g. a product id) so the condition keeps working when items move.</DialogDescription>
        </DialogHeader>
        {source?.source_id ? (
          <DataExplorer key={source.source_id} source={{ id: source.source_id, key, type: 'json_url' }} selectedPath={path} onSelect={setPath} onUse={(p) => onPick(`data.${p}`, familyOfSample(sample(p)))} maxHeightClass="max-h-80" />
        ) : (
          <Skeleton className="h-40" />
        )}
        {path && (
          <p className="rounded-md border bg-muted/30 px-2 py-1.5 font-mono text-[12px] break-all" aria-live="polite">
            data.{path}
          </p>
        )}
        <DialogFooter className="sm:justify-between">
          <Button type="button" variant="ghost" onClick={() => onPick(`data.${key}`, 'freshness')}>
            Check the data status instead
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={!path} onClick={() => path && onPick(`data.${path}`, familyOfSample(sample(path)))}>
              Use this value
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
